// src/utils/regionTree.ts
//
// Pure helpers for the region hierarchy: flat yak_regions rows in,
// nested tree out. Kept free of React/Supabase so they're unit-testable
// (the DB stores an adjacency list; all shaping happens client-side).

import type { YakRegion } from '../types/yak'

export interface RegionTreeNode {
  region: YakRegion
  children: RegionTreeNode[]
}

// Flat rows -> nested tree. Siblings are sorted by name (stable render
// order); rows pointing at a parent that isn't in the input are treated
// as roots rather than dropped, so a partial fetch never hides nodes.
export function buildRegionTree(rows: YakRegion[]): RegionTreeNode[] {
  const nodes = new Map<string, RegionTreeNode>()
  for (const region of rows) {
    nodes.set(region.id, { region, children: [] })
  }

  const roots: RegionTreeNode[] = []
  for (const node of nodes.values()) {
    const parentId = node.region.parent_region_id
    const parent = parentId ? nodes.get(parentId) : undefined
    if (parent) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  const byName = (a: RegionTreeNode, b: RegionTreeNode) =>
    a.region.name.localeCompare(b.region.name)
  const sortChildren = (list: RegionTreeNode[]) => {
    list.sort(byName)
    for (const node of list) sortChildren(node.children)
  }
  sortChildren(roots)

  return roots
}

// Depth-first flatten for indented rendering.
export function flattenRegionTree(
  tree: RegionTreeNode[],
  depth = 0
): Array<{ region: YakRegion; depth: number }> {
  const rows: Array<{ region: YakRegion; depth: number }> = []
  for (const node of tree) {
    rows.push({ region: node.region, depth })
    rows.push(...flattenRegionTree(node.children, depth + 1))
  }
  return rows
}

// Ids of a node and all its descendants — the "Move under…" picker
// excludes this set so a node can never be offered its own subtree.
export function collectSubtreeIds(
  rows: YakRegion[],
  rootId: string
): Set<string> {
  const childrenByParent = new Map<string, YakRegion[]>()
  for (const region of rows) {
    if (!region.parent_region_id) continue
    const siblings = childrenByParent.get(region.parent_region_id) || []
    siblings.push(region)
    childrenByParent.set(region.parent_region_id, siblings)
  }

  const ids = new Set<string>([rootId])
  const queue = [rootId]
  while (queue.length > 0) {
    const id = queue.shift() as string
    for (const child of childrenByParent.get(id) || []) {
      if (!ids.has(child.id)) {
        ids.add(child.id)
        queue.push(child.id)
      }
    }
  }
  return ids
}
